import type { GameServerInfo, LeaderboardEntry, PlayerProfile } from "@mmotdg/shared";
import { TUTORIAL_LEVELS, TUTORIAL_STAGES_TOTAL } from "@mmotdg/shared";
import {
  getMyProfile,
  getQueueStatus,
  getLeaderboard,
  joinQueue,
  leaveQueue,
  listServers,
  startPractice,
} from "../api/AuthClient.js";
import { appState, clearSession } from "../state/AppState.js";
import { icon } from "./icons.js";
import { mountFriendsBell } from "./social/FriendsPanel.js";
import { mountNotificationBell } from "./social/NotificationBell.js";
import { towerThumbnailHtml } from "./TowerThumbnail.js";

type Tab = "play" | "profile" | "rankings";

let currentTab: Tab = "play";
let pollHandle: number | null = null;
let inQueueMode: "arena" | "coop" | null = null;
let cachedServers: GameServerInfo[] = [];
let serversExpanded = false;
let unmountFriends: (() => void) | null = null;
let unmountBell: (() => void) | null = null;
let cachedTutorialStage: number | null = null;

export function renderLobbyScreen(container: HTMLElement): void {
  unmountFriends?.();
  unmountBell?.();
  unmountFriends = null;
  unmountBell = null;

  container.innerHTML = `
    <div class="top-bar">
      <div class="brand">Wardlines</div>
      <div class="user">
        <div id="notif-slot"></div>
        <div id="friends-slot"></div>
        <span id="user-label" style="color: var(--text-secondary);"></span>
        <button id="logout-btn">Sign out</button>
      </div>
    </div>
    <div class="lobby-main">
      <div class="tabs">
        <div class="tab ${currentTab === "play" ? "active" : ""}" data-tab="play">Play</div>
        <div class="tab ${currentTab === "profile" ? "active" : ""}" data-tab="profile">Profile</div>
        <div class="tab ${currentTab === "rankings" ? "active" : ""}" data-tab="rankings">Rankings</div>
      </div>
      <div class="tab-content" id="tab-content"></div>
    </div>
  `;

  const user = appState.get().user;
  if (user) container.querySelector("#user-label")!.textContent = user.username;

  container.querySelector("#logout-btn")!.addEventListener("click", () => {
    stopPolling();
    unmountFriends?.();
    unmountBell?.();
    unmountFriends = null;
    unmountBell = null;
    cachedTutorialStage = null;
    clearSession();
  });

  container.querySelectorAll<HTMLDivElement>(".tab").forEach((tab) => {
    tab.addEventListener("click", () => {
      currentTab = tab.dataset.tab as Tab;
      renderLobbyScreen(container);
    });
  });

  unmountBell = mountNotificationBell(container.querySelector<HTMLDivElement>("#notif-slot")!);
  unmountFriends = mountFriendsBell(container.querySelector<HTMLDivElement>("#friends-slot")!);

  renderTabContent(container);
}

function renderTabContent(container: HTMLElement): void {
  const target = container.querySelector<HTMLDivElement>("#tab-content")!;
  if (currentTab === "play") renderPlayTab(target);
  else if (currentTab === "profile") renderProfileTab(target);
  else renderRankingsTab(target);
}

function renderPlayTab(target: HTMLDivElement): void {
  target.innerHTML = `<div class="panel" style="max-width: 760px; margin: 0 auto;">Loading...</div><div id="queue-overlay"></div>`;
  void hydratePlayTab(target);
}

async function hydratePlayTab(target: HTMLDivElement): Promise<void> {
  let stage = cachedTutorialStage;
  if (stage === null) {
    try {
      const profile = await getMyProfile();
      stage = profile.tutorialStage;
      cachedTutorialStage = stage;
    } catch {
      stage = TUTORIAL_STAGES_TOTAL;
    }
  }
  if (stage < TUTORIAL_STAGES_TOTAL) renderTrainingTab(target, stage);
  else renderModeSelection(target);
}

function renderTrainingTab(target: HTMLDivElement, stage: number): void {
  const progressPct = Math.round((stage / TUTORIAL_STAGES_TOTAL) * 100);
  const steps = TUTORIAL_LEVELS.map((level) => {
    const completed = stage >= level.id;
    const current = !completed && stage + 1 === level.id;
    const locked = !completed && !current;
    const stateClass = completed ? "completed" : current ? "current" : "locked";
    const statusLabel = completed ? "Cleared" : current ? "Next up" : "Locked";
    const nodeContent = completed
      ? icon("check", 18)
      : locked
        ? icon("lock", 16)
        : String(level.id);

    let action = "";
    if (current) {
      action = `<button class="primary tutorial-start" data-level="${level.id}">Start <span style="display:inline-flex;margin-left:6px;">${icon("arrowRight", 14)}</span></button>`;
    } else if (completed) {
      action = `<button class="tutorial-start" data-level="${level.id}" style="background: transparent; border: 1px solid var(--line-strong); color: var(--text-secondary);">Replay</button>`;
    }

    const towerMuted = locked;
    const rewardMuted = !current && !completed;
    const towers = level.allowedTowers
      .map(
        (t) =>
          `<span class="tower-chip">${towerThumbnailHtml(t, { size: 28, muted: towerMuted })}<span>${escapeHtml(t)}</span></span>`,
      )
      .join("");
    const rewards = level.rewards
      .map(
        (r) =>
          `<span class="reward-chip">${towerThumbnailHtml(r, { size: 28, muted: rewardMuted })}<span>${escapeHtml(r)}</span></span>`,
      )
      .join("");

    return `
      <div class="tutorial-step ${stateClass}">
        <div class="rail"><span class="node">${nodeContent}</span></div>
        <div class="card">
          <div class="card-head">
            <h3>${escapeHtml(level.title)}</h3>
            <span class="status">${statusLabel}</span>
          </div>
          ${action ? `<div class="card-action">${action}</div>` : ""}
          <p class="card-subtitle">${escapeHtml(level.subtitle)}</p>
          <div class="card-meta">
            <div class="meta-block">
              <span class="meta-label">Towers</span>
              <div class="chip-row">${towers}</div>
            </div>
            <div class="meta-block">
              <span class="meta-label">Survive</span>
              <span style="color: var(--text-primary); font-weight: 600;">${level.requiredWaves} waves</span>
            </div>
            <div class="meta-block">
              <span class="meta-label">Reward</span>
              <div class="chip-row">${rewards}</div>
            </div>
          </div>
        </div>
      </div>
    `;
  }).join("");

  target.innerHTML = `
    <div class="panel" style="max-width: 820px; margin: 0 auto;">
      <h2 class="panel-title">Training</h2>
      <p class="panel-subtitle">Clear the four training missions to unlock every tower and access ranked play.</p>
      <div class="tutorial-progress">
        <span>Progress</span>
        <div class="bar"><span style="width: ${progressPct}%;"></span></div>
        <span>${stage}/${TUTORIAL_STAGES_TOTAL}</span>
      </div>
      <div class="tutorial-path">${steps}</div>
    </div>
    <div id="queue-overlay"></div>
  `;

  target.querySelectorAll<HTMLButtonElement>(".tutorial-start").forEach((btn) => {
    btn.addEventListener("click", () => {
      const level = Number(btn.dataset.level);
      if (Number.isInteger(level)) void startTutorialMatch(btn, level);
    });
  });
}

function renderModeSelection(target: HTMLDivElement): void {
  target.innerHTML = `
    <div class="panel" style="max-width: 760px; margin: 0 auto;">
      <h2 class="panel-title">Choose a mode</h2>
      <p class="panel-subtitle">Ranked play, custom rooms or solo training</p>
      <div class="lobby-grid">
        <div class="mode-card" data-mode="arena">
          <h3>Arena</h3>
          <p>1v1 competitive — defeat your opponent by sending mobs and defending your base.</p>
        </div>
        <div class="mode-card" data-mode="coop">
          <h3>Coop Survival</h3>
          <p>Team up against endless waves on a shared map.</p>
        </div>
        <div class="mode-card" data-mode="practice">
          <h3>Practice</h3>
          <p>Solo training, infinite waves. No queue, no ranking — just go as far as you can.</p>
        </div>
        <div class="mode-card" data-mode="rooms">
          <h3>Custom rooms</h3>
          <p>Browse open rooms, host your own with custom stake, or join via invite code.</p>
        </div>
      </div>
      <div style="margin-top: 24px; display: flex; justify-content: flex-end;">
        <button id="servers-toggle" class="servers-toggle" type="button">${serversExpanded ? "Hide servers" : "Show servers"}</button>
      </div>
      <div id="servers-wrap" style="margin-top: 8px; display: ${serversExpanded ? "block" : "none"};">
        <div id="server-list" style="min-height: 40px;"></div>
      </div>
    </div>
    <div id="queue-overlay"></div>
  `;

  const toggle = target.querySelector<HTMLButtonElement>("#servers-toggle");
  if (toggle) {
    toggle.addEventListener("click", () => {
      serversExpanded = !serversExpanded;
      const wrap = target.querySelector<HTMLDivElement>("#servers-wrap");
      if (wrap) wrap.style.display = serversExpanded ? "block" : "none";
      toggle.textContent = serversExpanded ? "Hide servers" : "Show servers";
      if (serversExpanded) void refreshServers(target);
    });
  }

  target.querySelectorAll<HTMLDivElement>(".mode-card").forEach((card) => {
    card.addEventListener("click", () => {
      const mode = card.dataset.mode as "arena" | "coop" | "practice" | "rooms";
      if (mode === "practice") {
        void startPracticeMatch(card);
      } else if (mode === "rooms") {
        appState.set({ screen: "room_browser" });
      } else {
        void startQueue(mode);
      }
    });
  });

  if (serversExpanded) void refreshServers(target);
}

async function startPracticeMatch(card: HTMLDivElement): Promise<void> {
  card.classList.add("disabled");
  try {
    const { assignment } = await startPractice();
    appState.set({
      screen: "match",
      matchAssignment: {
        roomId: assignment.roomId,
        mode: assignment.mode,
        serverId: assignment.serverId,
        serverHost: assignment.serverHost,
        serverPort: assignment.serverPort,
        tutorialLevel: assignment.tutorialLevel,
      },
    });
  } catch (err) {
    card.classList.remove("disabled");
    showToast(`Failed to start practice: ${err instanceof Error ? err.message : "PRACTICE_FAILED"}`);
  }
}

async function startTutorialMatch(btn: HTMLButtonElement, level: number): Promise<void> {
  btn.disabled = true;
  try {
    const { assignment } = await startPractice(level);
    appState.set({
      screen: "match",
      matchAssignment: {
        roomId: assignment.roomId,
        mode: assignment.mode,
        serverId: assignment.serverId,
        serverHost: assignment.serverHost,
        serverPort: assignment.serverPort,
        tutorialLevel: assignment.tutorialLevel,
      },
    });
  } catch (err) {
    btn.disabled = false;
    showToast(`Failed to start training: ${err instanceof Error ? err.message : "TUTORIAL_FAILED"}`);
  }
}

function showToast(message: string): void {
  const overlay = document.createElement("div");
  overlay.className = "toast";
  overlay.textContent = message;
  document.body.appendChild(overlay);
  setTimeout(() => overlay.remove(), 3000);
}

export function invalidateTutorialCache(): void {
  cachedTutorialStage = null;
}

async function refreshServers(target: HTMLDivElement): Promise<void> {
  const list = target.querySelector<HTMLDivElement>("#server-list");
  if (!list) return;
  try {
    const { servers } = await listServers();
    cachedServers = servers;
    if (servers.length === 0) {
      list.innerHTML = `<div class="empty" style="padding: 20px;">No game servers online</div>`;
      return;
    }
    list.innerHTML = servers
      .map(
        (s) => `
      <div class="server-row">
        <div>
          <div style="font-weight: 600;">${escapeHtml(s.id)}</div>
          <div class="meta">${escapeHtml(s.region)} · ${s.currentPlayers}/${s.maxPlayers} players · ${Math.round(s.load * 100)}% load</div>
        </div>
        <div class="meta">${s.status}</div>
      </div>
    `,
      )
      .join("");
  } catch (err) {
    list.innerHTML = `<div class="empty" style="padding: 20px;">Failed to load servers</div>`;
  }
}

async function startQueue(mode: "arena" | "coop"): Promise<void> {
  inQueueMode = mode;
  try {
    await joinQueue(mode);
  } catch (err) {
    inQueueMode = null;
    return;
  }
  appState.set({ screen: "queue" });
  renderQueueOverlay(mode);
  pollHandle = window.setInterval(() => void pollQueue(), 1500);
}

function renderQueueOverlay(mode: "arena" | "coop"): void {
  const overlay = document.createElement("div");
  overlay.className = "screen";
  overlay.style.background = "rgba(7, 9, 15, 0.85)";
  overlay.style.zIndex = "100";
  overlay.id = "queue-screen";
  overlay.innerHTML = `
    <div class="panel" style="text-align: center; min-width: 320px;">
      <h2 class="panel-title">Searching for ${mode === "arena" ? "Arena" : "Coop"} match</h2>
      <div class="queue-status">
        <div class="spinner"></div>
        <div id="queue-detail">Waiting for opponents...</div>
      </div>
      <div class="btn-row" style="justify-content: center;">
        <button id="cancel-queue-btn" class="danger">Cancel</button>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);
  overlay.querySelector("#cancel-queue-btn")!.addEventListener("click", () => void cancelQueue());
}

async function cancelQueue(): Promise<void> {
  await leaveQueue().catch(() => {});
  stopPolling();
  document.getElementById("queue-screen")?.remove();
  appState.set({ screen: "lobby" });
  inQueueMode = null;
}

async function pollQueue(): Promise<void> {
  try {
    const status = await getQueueStatus();
    if (status.status === "matched" && status.assignment) {
      stopPolling();
      document.getElementById("queue-screen")?.remove();
      appState.set({
        screen: "match",
        matchAssignment: {
          roomId: status.assignment.roomId,
          mode: status.assignment.mode,
          serverId: status.assignment.serverId,
          serverHost: status.assignment.serverHost,
          serverPort: status.assignment.serverPort,
          tutorialLevel: null,
        },
      });
    } else {
      const detail = document.getElementById("queue-detail");
      if (detail && status.queueStats) {
        detail.textContent = `In queue · ${inQueueMode === "arena" ? status.queueStats.arena : status.queueStats.coop} players waiting`;
      }
    }
  } catch {
    // ignore
  }
}

function stopPolling(): void {
  if (pollHandle !== null) {
    clearInterval(pollHandle);
    pollHandle = null;
  }
}

async function renderProfileTab(target: HTMLDivElement): Promise<void> {
  target.innerHTML = `<div class="panel" style="max-width: 760px; margin: 0 auto;">Loading profile...</div>`;
  try {
    const profile = await getMyProfile();
    target.innerHTML = renderProfile(profile);
  } catch {
    target.innerHTML = `<div class="empty">Failed to load profile</div>`;
  }
}

function renderProfile(profile: PlayerProfile): string {
  const arenaWR =
    profile.stats.arenaWins + profile.stats.arenaLosses > 0
      ? Math.round((profile.stats.arenaWins / (profile.stats.arenaWins + profile.stats.arenaLosses)) * 100)
      : 0;
  return `
    <div class="panel" style="max-width: 760px; margin: 0 auto;">
      <h2 class="panel-title">${escapeHtml(profile.username)}</h2>
      <p class="panel-subtitle">Level ${profile.level} · ${profile.xp} XP / ${profile.xpForNextLevel}</p>
      <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-top: 16px;">
        <div class="list-row">
          <div>Arena rating <span class="tier-badge tier-${profile.ratings.arena.tier}">${profile.ratings.arena.tier}</span></div>
          <div style="font-weight: 600; font-variant-numeric: tabular-nums;">${Math.round(profile.ratings.arena.rating)}</div>
        </div>
        <div class="list-row">
          <div>Coop rating <span class="tier-badge tier-${profile.ratings.coop.tier}">${profile.ratings.coop.tier}</span></div>
          <div style="font-weight: 600; font-variant-numeric: tabular-nums;">${Math.round(profile.ratings.coop.rating)}</div>
        </div>
        <div class="list-row">
          <div>Matches played</div>
          <div style="font-weight: 600;">${profile.stats.matchesPlayed}</div>
        </div>
        <div class="list-row">
          <div>Arena winrate</div>
          <div style="font-weight: 600;">${arenaWR}%</div>
        </div>
        <div class="list-row">
          <div>Best coop wave</div>
          <div style="font-weight: 600;">${profile.stats.coopBestWave}</div>
        </div>
        <div class="list-row">
          <div>Towers unlocked</div>
          <div style="font-weight: 600;">${profile.unlockedTowers.length}/8</div>
        </div>
      </div>
      <h3 style="margin-top: 24px;">Loadout</h3>
      <div style="display: flex; gap: 8px; flex-wrap: wrap;">
        ${profile.loadout.map((t) => `<div class="tower-tile" style="cursor: default;">${icon(t, 28)}<div class="name">${escapeHtml(t)}</div></div>`).join("")}
      </div>
    </div>
  `;
}

async function renderRankingsTab(target: HTMLDivElement): Promise<void> {
  target.innerHTML = `<div class="panel" style="max-width: 760px; margin: 0 auto;">Loading rankings...</div>`;
  try {
    const { entries } = await getLeaderboard("arena");
    target.innerHTML = `
      <div class="panel" style="max-width: 760px; margin: 0 auto;">
        <h2 class="panel-title">Arena leaderboard</h2>
        ${entries.length === 0 ? `<div class="empty">No ranked players yet</div>` : entries.map(renderLeaderboardRow).join("")}
      </div>
    `;
  } catch {
    target.innerHTML = `<div class="empty">Failed to load rankings</div>`;
  }
}

function renderLeaderboardRow(entry: LeaderboardEntry): string {
  return `
    <div class="list-row">
      <div style="display:flex; gap: 12px; align-items: center;">
        <div style="width: 40px; color: var(--text-secondary); font-weight: 600;">#${entry.rank}</div>
        <div>${escapeHtml(entry.username)}</div>
        <span class="tier-badge tier-${entry.tier}">${entry.tier}</span>
      </div>
      <div style="font-weight: 600; font-variant-numeric: tabular-nums;">${Math.round(entry.rating)}</div>
    </div>
  `;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    c === "&" ? "&amp;" :
    c === "<" ? "&lt;" :
    c === ">" ? "&gt;" :
    c === '"' ? "&quot;" : "&#39;",
  );
}
